import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";

import {
  currentUserRetryDelay,
  shouldRetryCurrentUser,
} from "../components/slow-start.js";
import { readLoggedOnThisComputer } from "./logged-here.js";
import { getSharedDeviceRoster, isUnboundRoster } from "./quick-access-api.js";
import {
  forgetPerson,
  mergePeople,
  parsePeople,
  peopleStorageKey,
  rememberPerson,
  serialisePeople,
  type ComputerPerson,
  type PersonTile,
} from "./people-on-computer.js";

const listeners = new Set<() => void>();
const volatile = new Map<string, string>();
const parsed = new Map<
  string,
  { people: ComputerPerson[]; raw: string | null }
>();

function readRaw(deviceId: string): string | null {
  const key = peopleStorageKey(deviceId);
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return volatile.get(key) ?? null;
  }
}

function writeRaw(deviceId: string, value: string | null): void {
  const key = peopleStorageKey(deviceId);
  try {
    if (value === null) globalThis.localStorage.removeItem(key);
    else globalThis.localStorage.setItem(key, value);
  } catch {
    if (value === null) volatile.delete(key);
    else volatile.set(key, value);
  }
  for (const listener of listeners) listener();
}

export function readPeople(deviceId: string): ComputerPerson[] {
  const raw = readRaw(deviceId);
  const cached = parsed.get(deviceId);
  if (cached && cached.raw === raw) return cached.people;
  const people = parsePeople(raw);
  parsed.set(deviceId, { people, raw });
  return people;
}

export function addPerson(deviceId: string, person: ComputerPerson): void {
  const current = readPeople(deviceId);
  const next = rememberPerson(current, person);
  const raw = serialisePeople(next);
  if (raw !== readRaw(deviceId)) writeRaw(deviceId, raw);
}

export function removePerson(deviceId: string, userId: string): void {
  writeRaw(
    deviceId,
    serialisePeople(forgetPerson(readPeople(deviceId), userId)),
  );
}

export function clearPeople(deviceId: string): void {
  writeRaw(deviceId, null);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

const none: ComputerPerson[] = [];

export function usePeople(deviceId: string | null): ComputerPerson[] {
  const snapshot = useCallback(
    () => (deviceId ? readPeople(deviceId) : none),
    [deviceId],
  );
  return useSyncExternalStore(subscribe, snapshot, () => none);
}

export interface PeopleOnComputer {
  deviceId: string | null;
  error: unknown;
  loading: boolean;
  noDevice: boolean;
  refetch: () => void;
  orgId: string | null;
  orgName: string | null;
  tiles: PersonTile[];
}

export function usePeopleOnComputer(): PeopleOnComputer {
  const roster = useQuery({
    queryFn: getSharedDeviceRoster,
    queryKey: ["shared-device-roster"],
    retry: shouldRetryCurrentUser,
    retryDelay: currentUserRetryDelay,
    staleTime: 60_000,
  });
  const deviceId = roster.data?.device_id ?? null;
  const local = usePeople(deviceId);
  const tiles = useMemo(
    () =>
      mergePeople(
        local,
        roster.data?.profiles ?? [],
        readLoggedOnThisComputer(),
      ),
    [local, roster.data],
  );
  return {
    deviceId,
    error: roster.error,
    loading: roster.isPending,
    noDevice: isUnboundRoster(roster.data, roster.error),
    refetch: () => void roster.refetch(),
    orgId: roster.data?.org_id ?? null,
    orgName: roster.data?.org_name ?? null,
    tiles,
  };
}
